import { Language } from '../context/LanguageContext';

interface LocationData {
  country?: string;
  countryCode?: string;
}

export class LocationDetectionService {
  private static readonly TIMEOUT_MS = 5000;
  
  private static readonly COUNTRY_TO_LANGUAGE_MAP: Record<string, Language> = {
    // German-speaking countries
    'DE': 'de', // Germany
    'AT': 'de', // Austria  
    'CH': 'de', // Switzerland (majority German)
    'LI': 'de', // Liechtenstein
    'LU': 'de', // Luxembourg (German is one of official languages)
    
    // French-speaking countries
    'FR': 'fr', // France
    'BE': 'fr', // Belgium (French/Dutch, but includes French speakers)
    'MC': 'fr', // Monaco
    'CA': 'fr', // Canada (French/English - we could enhance this with more specific detection)
    
    // Chinese-speaking countries/regions
    'CN': 'zh', // China
    'TW': 'zh', // Taiwan
    'HK': 'zh', // Hong Kong
    'MO': 'zh', // Macau
    'SG': 'zh', // Singapore (multi-language, but includes Chinese)
    
    // English is default for all other countries
  };

  static async detectUserLanguage(): Promise<Language> {
    try {
      // In development (localhost), prioritize browser language to avoid CORS issues
      const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      
      if (isDevelopment) {
        // Use browser language detection first in development
        const browserLanguage = this.detectBrowserLanguage();
        if (browserLanguage !== 'en') {
          console.log(`Development mode: Detected browser language: ${browserLanguage}`);
          return browserLanguage;
        }
      } else {
        // In production, try IP-based geolocation first
        const country = await this.detectCountryFromIP();
        if (country) {
          const language = this.mapCountryToLanguage(country);
          if (language !== 'en') {
            console.log(`Detected country: ${country}, setting language to: ${language}`);
            return language;
          }
        }

        // Fallback to browser language detection
        const browserLanguage = this.detectBrowserLanguage();
        if (browserLanguage !== 'en') {
          console.log(`Detected browser language: ${browserLanguage}`);
          return browserLanguage;
        }
      }

      // Default to English
      console.log('Defaulting to English language');
      return 'en';
      
    } catch (error) {
      console.warn('Language detection failed, defaulting to English:', error);
      return 'en';
    }
  }

  private static async detectCountryFromIP(): Promise<string | null> {
    // Try ipapi.co first (most reliable for CORS)
    try {
      const response = await this.fetchWithTimeout('https://ipapi.co/country_code/');
      if (response.ok) {
        const countryCode = await response.text();
        if (countryCode && countryCode.length === 2) {
          return countryCode.trim().toUpperCase();
        }
      }
    } catch (error) {
      console.warn('ipapi.co failed:', error);
    }

    // Fallback to httpbin.org (provides IP info)
    try {
      const response = await this.fetchWithTimeout('https://httpbin.org/ip');
      if (response.ok) {
        const data = await response.json();
        if (data.origin) {
          // This gives us the IP, but we'd need another service to get country
          // For now, we'll skip this and go to browser detection
          console.log('Got IP from httpbin:', data.origin);
        }
      }
    } catch (error) {
      console.warn('httpbin.org failed:', error);
    }

    // Try FreeIPAPI as another fallback
    try {
      const response = await this.fetchWithTimeout('https://freeipapi.com/api/json');
      if (response.ok) {
        const data = await response.json();
        if (data.countryCode) {
          return data.countryCode.toUpperCase();
        }
      }
    } catch (error) {
      console.warn('FreeIPAPI failed:', error);
    }

    return null;
  }

  private static detectBrowserLanguage(): Language {
    try {
      // Get user's preferred language from browser
      const browserLang = navigator.language || (navigator as any).userLanguage;
      if (!browserLang) return 'en';

      // Extract language code (e.g., 'en-US' -> 'en', 'zh-CN' -> 'zh')
      const langCode = browserLang.split('-')[0].toLowerCase();

      // Map to our supported languages
      switch (langCode) {
        case 'de':
          return 'de';
        case 'fr':
          return 'fr';
        case 'zh':
          return 'zh';
        case 'en':
        default:
          return 'en';
      }
    } catch (error) {
      console.warn('Browser language detection failed:', error);
      return 'en';
    }
  }

  private static mapCountryToLanguage(countryCode: string): Language {
    return this.COUNTRY_TO_LANGUAGE_MAP[countryCode.toUpperCase()] || 'en';
  }

  private static async fetchWithTimeout(url: string): Promise<Response> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.TIMEOUT_MS);

    try {
      const response = await fetch(url, {
        signal: controller.signal,
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });
      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    }
  }

  // Public method to get current location info for debugging
  static async getLocationInfo(): Promise<LocationData> {
    try {
      const response = await this.fetchWithTimeout('https://ipapi.co/json/');
      if (response.ok) {
        const data = await response.json();
        return {
          country: data.country_name,
          countryCode: data.country_code,
        };
      }
    } catch (error) {
      console.warn('Failed to get location info:', error);
    }
    return {};
  }
}